import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Upload, FileText, CheckCircle, Loader2, Eye, Minus, Plus, X, MapPin, MapPinOff, SlidersHorizontal, ArrowDown } from 'lucide-react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useVerifyQRTokenMutation } from '../store/api/qrApi';
import { useGetPresignedUrlMutation } from '../store/api/uploadApi';
import { useCreatePaymentOrderMutation, useVerifyPaymentMutation } from '../store/api/paymentApi';
import { useCreatePrintRequestMutation } from '../store/api/documentApi';
import {
  useGetShopSettingsByOwnerQuery,
  ShopPricing,
  PaperSize,
  PaperType,
  Orientation,
  PAPER_TYPE_LABELS,
} from '../store/api/shopSettingsApi';
import FullScreenLoader from '../components/FullScreenLoader';
import { QRVerifyResponse, RazorpayPaymentSuccessResponse } from '../types';
import {
  expandZipToSupportedDocuments,
  isImageFile,
  isSupportedUploadFile,
  isZipUpload,
} from '../utils/documentPageCount';
import { pdfjsLib } from '../utils/pdfWorker';

const DEFAULT_PRICING: ShopPricing = {
  bw: { baseCharge: 5, perPageCharge: 2 },
  color: { baseCharge: 10, perPageCharge: 10 },
};

const DEFAULT_PAPER_SIZES: PaperSize[] = ['A4', 'A3', 'Letter'];
const DEFAULT_PAPER_TYPES: PaperType[] = ['normal'];

type ErrorWithOptionalFields = {
  message?: string;
  error?: string;
  data?: {
    error?: string;
  };
};

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as ErrorWithOptionalFields;
    return candidate.data?.error || candidate.error || candidate.message || fallback;
  }
  return fallback;
}

export default function UploadPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [verifyToken, { isLoading: verifying }] = useVerifyQRTokenMutation();
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const [createPaymentOrder] = useCreatePaymentOrderMutation();
  const [verifyPayment] = useVerifyPaymentMutation();
  const [createPrintRequest] = useCreatePrintRequestMutation();

  const [shopData, setShopData] = useState<QRVerifyResponse | null>(null);
  const [customerCoords, setCustomerCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [color, setColor] = useState<'bw' | 'color'>('bw');
  const [paperSize, setPaperSize] = useState<PaperSize>('A4');
  const [paperType, setPaperType] = useState<PaperType>('normal');
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'cod'>('online');
  const [manualPageCount, setManualPageCount] = useState<number | ''>('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhoneInput, setCustomerPhoneInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState({ title: 'Processing...', subtitle: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Location gate: intro primes the browser permission prompt; blocked shows the
  // stop screen when the shop requires location and it's denied / out of range.
  const [showLocationIntro, setShowLocationIntro] = useState(true);
  const [locationBlocked, setLocationBlocked] = useState(false);
  // Why the location gate is showing: 'denied' = permission blocked (show how-to-enable),
  // 'unavailable' = GPS timeout/off (show simple retry), 'outofrange' = away from shop.
  const [locationMode, setLocationMode] = useState<'denied' | 'unavailable' | 'outofrange'>('denied');
  const clientSessionIdRef = useRef('');
  const ownerId = shopData?.ownerId || '';
  const copies = quantity;
  const documentCount = files.length;
  const imageOnlySelection = files.length > 0 && files.every((file) => isImageFile(file));
  const createClientSessionId = () => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `scan-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };
  const parsedPhone = useMemo(() => {
    if (!customerPhoneInput) {
      return undefined;
    }
    return parsePhoneNumberFromString(customerPhoneInput, 'IN');
  }, [customerPhoneInput]);
  const isPhoneValid = Boolean(
    parsedPhone && parsedPhone.country === 'IN' && parsedPhone.isValid()
  );

  // Fetch shop settings when we have an ownerId
  const { data: shopSettings } = useGetShopSettingsByOwnerQuery(ownerId, {
    skip: !ownerId,
  });

  const pricing = shopSettings?.pricing || DEFAULT_PRICING;
  const advancedPricing = shopSettings?.advancedPricing;
  const supportedPaperSizes = shopSettings?.supportedPaperSizes || DEFAULT_PAPER_SIZES;
  const supportedPaperTypes = shopSettings?.supportedPaperTypes || DEFAULT_PAPER_TYPES;

  // Set default paper size when shop settings load
  useEffect(() => {
    if (supportedPaperSizes.length > 0 && !supportedPaperSizes.includes(paperSize)) {
      setPaperSize(supportedPaperSizes[0]);
    }
  }, [supportedPaperSizes, paperSize]);

  // Set default paper type when shop settings load
  useEffect(() => {
    if (supportedPaperTypes.length > 0 && !supportedPaperTypes.includes(paperType)) {
      setPaperType(supportedPaperTypes[0]);
    }
  }, [supportedPaperTypes, paperType]);

  type GeoResult =
    | { coords: { lat: number; lng: number } }
    | { error: 'denied' | 'unavailable' };

  const getGeolocation = (): Promise<GeoResult> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({ error: 'unavailable' });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ coords: { lat: pos.coords.latitude, lng: pos.coords.longitude } }),
        (err) =>
          // Only PERMISSION_DENIED means the user actually blocked us. A timeout or
          // POSITION_UNAVAILABLE (slow GPS lock, GPS off) is NOT a permission problem,
          // so it must not show the "enable permission" instructions.
          resolve({ error: err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable' }),
        // High accuracy + a generous timeout: Android Chrome's first GPS fix often
        // takes well over 8s. maximumAge accepts a recent cached fix to respond fast.
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 30000 },
      );
    });

  // Set up the per-session id once; verification is triggered after the user
  // acknowledges the location-permission intro (so the browser prompt isn't a surprise).
  useEffect(() => {
    if (!token) {
      setShowLocationIntro(false);
      setError(
        'No QR code token was found in this link. Please rescan the QR code from the print shop.'
      );
      return;
    }
    const clientSessionStorageKey = `qr-upload-client-session:${token}`;
    try {
      const stored = localStorage.getItem(clientSessionStorageKey);
      const id = stored || createClientSessionId();
      if (!stored) localStorage.setItem(clientSessionStorageKey, id);
      clientSessionIdRef.current = id;
    } catch {
      clientSessionIdRef.current = createClientSessionId();
    }
  }, [token]);

  const requestLocationAndVerify = useCallback(async () => {
    if (!token) return;
    setShowLocationIntro(false);
    setLocationBlocked(false);
    setError('');
    const geo = await getGeolocation();
    // No coordinates = permission denied / GPS unavailable. Location is mandatory to
    // upload, so block immediately (don't even verify) — "without location, no upload".
    if ('error' in geo) {
      setLocationMode(geo.error);
      setLocationBlocked(true);
      return;
    }
    const coords = geo.coords;
    setCustomerCoords(coords);
    try {
      const data = await verifyToken({
        token,
        clientSessionId: clientSessionIdRef.current,
        customerLat: coords.lat,
        customerLng: coords.lng,
      }).unwrap();
      setShopData(data);
    } catch (err: any) {
      const msg: string = err?.data?.error || err?.error || '';
      if (msg.toLowerCase().includes('location')) {
        // Backend rejected — out of range (shop has a location configured).
        setLocationMode('outofrange');
        setLocationBlocked(true);
      } else {
        setError(
          'This QR link is invalid or has expired. Please scan the latest QR code from the print shop and try again.'
        );
      }
    }
  }, [token, verifyToken]);

  // Generate file preview
  useEffect(() => {
    if (!previewFile) {
      setFilePreview(null);
      return;
    }
    if (previewFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target?.result as string);
      reader.readAsDataURL(previewFile);
    } else if (previewFile.type === 'application/pdf') {
      let cancelled = false;
      (async () => {
        try {
          const arrayBuffer = await previewFile.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            await page.render({ canvas, canvasContext: ctx, viewport }).promise;
            if (!cancelled) setFilePreview(canvas.toDataURL());
          } else {
            if (!cancelled) setFilePreview('pdf');
          }
        } catch {
          if (!cancelled) setFilePreview('pdf');
        }
      })();
      return () => { cancelled = true; };
    } else {
      setFilePreview('document');
    }
  }, [previewFile]);

  // Auto-detect page count for PDFs — only re-runs when the PDF file set changes
  const pdfFileKeys = files
    .filter((f) => f.type === 'application/pdf')
    .map((f) => `${f.name}:${f.size}:${f.lastModified}`)
    .join('|');

  useEffect(() => {
    if (!pdfFileKeys) return;
    const pdfFiles = files.filter((f) => f.type === 'application/pdf');
    let cancelled = false;
    (async () => {
      let total = 0;
      for (const f of pdfFiles) {
        try {
          const buf = await f.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
          total += pdf.numPages;
        } catch { /* fall through; user can enter manually */ }
      }
      if (!cancelled && total > 0) setManualPageCount(total);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfFileKeys]);

  const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30 MB
  const canSubmit = files.length > 0;


  const addSelectedFiles = useCallback(async (incoming: FileList | File[]) => {
    const nextFiles = Array.from(incoming);
    if (nextFiles.length === 0) {
      return;
    }

    const preparedFiles: File[] = [];
    const errors: string[] = [];

    for (const nextFile of nextFiles) {
      if (nextFile.size > MAX_FILE_SIZE) {
        errors.push(`${nextFile.name} exceeded the 30 MB limit`);
        continue;
      }

      if (!isSupportedUploadFile(nextFile)) {
        errors.push(`${nextFile.name} is not a supported PDF, DOCX, image, or ZIP file`);
        continue;
      }

      if (isZipUpload(nextFile)) {
        try {
          const extractedFiles = await expandZipToSupportedDocuments(nextFile);
          const oversizedEntries = extractedFiles.filter((file) => file.size > MAX_FILE_SIZE);
          if (oversizedEntries.length > 0) {
            errors.push(
              `${nextFile.name} contains files over 30 MB: ${oversizedEntries.map((file) => file.name).join(', ')}`
            );
            continue;
          }
          preparedFiles.push(...extractedFiles);
        } catch (err: unknown) {
          errors.push(`${nextFile.name}: ${getErrorMessage(err, 'Failed to read ZIP')}`);
        }
        continue;
      }

      preparedFiles.push(nextFile);
    }

    if (preparedFiles.length > 0) {
      setFiles((prev) => {
        const unique = [...prev];
        for (const nextFile of preparedFiles) {
          const exists = unique.some(
            (existing) =>
              existing.name === nextFile.name &&
              existing.size === nextFile.size &&
              existing.lastModified === nextFile.lastModified
          );
          if (!exists) {
            unique.push(nextFile);
          }
        }
        return unique;
      });
    }

    setError(errors.join(' | '));
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      addSelectedFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files?.length) {
        addSelectedFiles(e.dataTransfer.files);
      }
    },
    [addSelectedFiles]
  );

  const handleRemoveFile = (fileToRemove: File) => {
    setFiles((prev) =>
      prev.filter(
        (item) =>
          !(
            item.name === fileToRemove.name &&
            item.size === fileToRemove.size &&
            item.lastModified === fileToRemove.lastModified
          )
      )
    );

    if (
      previewFile &&
      previewFile.name === fileToRemove.name &&
      previewFile.size === fileToRemove.size &&
      previewFile.lastModified === fileToRemove.lastModified
    ) {
      setShowPreview(false);
      setPreviewFile(null);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const perPagePrice = useMemo(() => {
    if (advancedPricing?.perPagePricing) {
      const sizeConfig =
        advancedPricing.perPagePricing[paperSize] || advancedPricing.perPagePricing['A4'];
      const typeConfig = sizeConfig?.[paperType] || sizeConfig?.['normal'];
      return typeConfig?.[color] || 2;
    }
    return pricing[color].perPageCharge;
  }, [advancedPricing, pricing, color, paperSize, paperType]);

  const baseCharge = useMemo(() => {
    if (advancedPricing?.perPagePricing) {
      return advancedPricing.baseCharge;
    }
    return pricing[color].baseCharge;
  }, [advancedPricing, pricing, color]);

  const effectivePageCount = useMemo(() => {
    if (!canSubmit) return 0;
    if (imageOnlySelection) return documentCount;
    return typeof manualPageCount === 'number' && manualPageCount > 0 ? manualPageCount : 0;
  }, [canSubmit, imageOnlySelection, documentCount, manualPageCount]);

  const perCopyTotal = useMemo(() => {
    if (!canSubmit || effectivePageCount === 0) return 0;
    return baseCharge * documentCount + perPagePrice * effectivePageCount;
  }, [baseCharge, documentCount, perPagePrice, canSubmit, effectivePageCount]);

  const calculatePrice = useMemo(() => {
    if (documentCount === 0 || !canSubmit) {
      return 0;
    }

    return perCopyTotal * copies;
  }, [perCopyTotal, copies, documentCount, canSubmit]);

  const handleSubmit = async () => {
    if (!canSubmit || !customerName || !isPhoneValid || !shopData?.sessionToken) return;
    const selectedFiles = [...files];
    const requestSeed = Date.now();

    const uploadFiles = async () => {
      const uploadedFiles: Array<{
        file: File;
        fileKey: string;
        pageCount: number;
      }> = [];

      const nonImageCount = selectedFiles.filter((f) => !isImageFile(f)).length;
      const totalManualPages = typeof manualPageCount === 'number' && manualPageCount > 0 ? manualPageCount : 1;
      const pagesPerNonImage = nonImageCount > 0 ? Math.max(1, Math.round(totalManualPages / nonImageCount)) : 1;

      for (let index = 0; index < selectedFiles.length; index++) {
        const selectedFile = selectedFiles[index];
        setLoadingMessage({
          title: `Uploading ${index + 1} of ${selectedFiles.length}...`,
          subtitle: selectedFile.name,
        });

        const { url, fields, fileKey } = await getPresignedUrl({
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          qrSessionToken: shopData.sessionToken,
          ...(customerCoords ? { customerLat: customerCoords.lat, customerLng: customerCoords.lng } : {}),
        }).unwrap();

        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
        formData.append('file', selectedFile);

        const uploadResponse = await fetch(url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${selectedFile.name}`);
        }

        uploadedFiles.push({
          file: selectedFile,
          fileKey,
          pageCount: isImageFile(selectedFile) ? 1 : pagesPerNonImage,
        });
      }

      return uploadedFiles;
    };

    const processSelectedFiles = async (
      analyzedFiles: Array<{ file: File; fileKey: string; pageCount: number }>,
      paymentDetails?: {
        orderId: string;
        paymentId: string;
      }
    ) => {
      for (let index = 0; index < analyzedFiles.length; index++) {
        const selectedFile = analyzedFiles[index];
        setLoadingMessage({
          title: `Creating ${index + 1} of ${analyzedFiles.length}...`,
          subtitle: selectedFile.file.name,
        });

        await createPrintRequest({
          fileKey: selectedFile.fileKey,
          fileName: selectedFile.file.name,
          ownerId: shopData.ownerId,
          qrSessionToken: shopData.sessionToken,
          userId: `customer-${requestSeed}-${index + 1}`,
          userName: customerName,
          userPhone: parsedPhone!.number,
          color,
          paymentMethod,
          pageCount: selectedFile.pageCount,
          paperSize,
          paperType,
          orientation,
          copies,
          razorpayOrderId: paymentDetails?.orderId,
          razorpayPaymentId: paymentDetails?.paymentId,
        }).unwrap();
      }
    };

    setUploading(true);
    setLoadingMessage({ title: 'Preparing upload...', subtitle: 'Please wait' });
    setError('');

    try {
      const uploadedFiles = await uploadFiles();
      const totalPageCount = uploadedFiles.reduce((sum, file) => sum + file.pageCount, 0);

      if (paymentMethod === 'online') {
        setLoadingMessage({
          title: 'Preparing payment...',
          subtitle: 'Setting up secure checkout',
        });

        const order = await createPaymentOrder({
          pageCount: totalPageCount,
          documentCount: uploadedFiles.length,
          copies,
          printType: color,
          paperSize,
          paperType,
          fileName:
            uploadedFiles.length === 1
              ? uploadedFiles[0].file.name
              : `${uploadedFiles.length} documents`,
          userId: `customer-${requestSeed}`,
          ownerId: shopData.ownerId,
        }).unwrap();

        // Hide loader while Razorpay modal is open
        setUploading(false);

        const rzp = new window.Razorpay({
          key: order.keyId,
          amount: order.amount,
          order_id: order.orderId,
          name: shopData.shopName,
          description: `Print ${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'document' : 'documents'}`,
          handler: async (response: RazorpayPaymentSuccessResponse) => {
            setUploading(true);
            setLoadingMessage({ title: 'Verifying payment...', subtitle: 'Almost done' });

            try {
              const paymentVerification = await verifyPayment(response).unwrap();
              await processSelectedFiles(uploadedFiles, {
                orderId: order.orderId,
                paymentId: paymentVerification.paymentId,
              });
              setSuccess(true);
              resetForm();
            } catch (err: unknown) {
              setError(getErrorMessage(err, 'Payment verification failed'));
            } finally {
              setUploading(false);
            }
          },
          modal: {
            ondismiss: () => {
              setUploading(false);
              setError('Payment cancelled');
            },
          },
        });
        rzp.open();
      } else {
        await processSelectedFiles(uploadedFiles);
        setSuccess(true);
        resetForm();
        setUploading(false);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Upload failed'));
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFiles([]);
    setPreviewFile(null);
    setFilePreview(null);
    setCustomerName('');
    setCustomerPhoneInput('');
    setQuantity(1);
    setManualPageCount('');
  };

  // 1. Location-permission intro — primes the user before the browser prompt
  if (showLocationIntro && token) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="relative w-full max-w-sm rounded-2xl bg-slate-800 border border-slate-700/50 shadow-xl p-6 text-center">
          <button
            type="button"
            onClick={requestLocationAndVerify}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-200"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-indigo-500/10">
            <MapPin size={26} className="text-indigo-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-100">Allow your location to continue</h2>
          <p className="mt-2 text-xs text-slate-400">
            This print shop confirms you're at the counter before accepting uploads.
          </p>
          <div className="mt-5 space-y-2.5 text-left">
            <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5">
              <CheckCircle size={18} className="text-emerald-400 shrink-0" />
              <span className="text-sm text-slate-200">Allow while visiting the site</span>
            </div>
            <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5">
              <CheckCircle size={18} className="text-emerald-400 shrink-0" />
              <span className="text-sm text-slate-200">Allow this time</span>
            </div>
          </div>
          <p className="mt-4 inline-block rounded-full bg-indigo-500/10 px-3 py-1 text-xs text-indigo-300">
            Select any one option to proceed
          </p>
          <button
            type="button"
            onClick={requestLocationAndVerify}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
          >
            <MapPin size={16} /> Enable location access
          </button>
        </div>
      </div>
    );
  }

  // 2. Stop screen — shop requires location but it's denied or out of range
  if (locationBlocked) {
    const outOfRange = locationMode === 'outofrange';
    const unavailable = locationMode === 'unavailable';
    const heading = outOfRange
      ? "You're not at the shop"
      : unavailable
        ? "Couldn't get your location"
        : 'Location access is blocked';
    const subtitle = outOfRange
      ? 'You can only upload while at the print shop. Please come to the counter and try again.'
      : unavailable
        ? 'Make sure your GPS / Location is turned on, step outside if you have weak signal, then try again.'
        : 'Enable it to continue uploading your documents';
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="relative w-full max-w-sm rounded-2xl bg-slate-800 border border-slate-700/50 shadow-xl p-6">
          <button
            type="button"
            onClick={requestLocationAndVerify}
            className="absolute top-4 right-4 text-slate-400 hover:text-slate-200"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          <div className="text-center">
            <div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
              <MapPinOff size={26} className="text-amber-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-100">{heading}</h2>
            <p className="mt-1.5 text-xs text-slate-400">{subtitle}</p>
          </div>

          {locationMode === 'denied' && (
            <>
              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-700" />
                <span className="text-[10px] font-semibold tracking-widest text-slate-500">HOW TO ENABLE</span>
                <div className="h-px flex-1 bg-slate-700" />
              </div>

              {/* Step 1 */}
              <div className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">1</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-300">
                    Tap the <span className="text-slate-100 font-medium">site info</span> icon at the left of the address bar
                  </p>
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2">
                    <SlidersHorizontal size={15} className="text-slate-400 shrink-0" />
                    <span className="text-xs text-slate-400 truncate">statioprint.statiomart.com</span>
                  </div>
                </div>
              </div>

              <div className="my-2 flex justify-center">
                <ArrowDown size={18} className="text-indigo-400" />
              </div>

              {/* Location toggle visual */}
              <div className="flex items-center justify-between rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <MapPin size={15} className="text-slate-400 shrink-0" />
                  <span className="text-sm text-slate-200">Location</span>
                </div>
                <div className="relative h-5 w-9 rounded-full bg-indigo-600">
                  <span className="absolute right-0.5 top-0.5 h-4 w-4 rounded-full bg-white" />
                </div>
              </div>
              <p className="mt-1 text-center text-[11px] text-slate-500">Toggle to Allow</p>

              {/* Step 2 */}
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">2</span>
                <p className="text-sm text-slate-300">
                  Come back here and tap <span className="text-slate-100 font-medium">Try again</span>
                </p>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={requestLocationAndVerify}
            className="mt-5 w-full rounded-lg bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (verifying) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen gap-4 bg-slate-950">
        <Loader2 size={32} className="text-indigo-600 animate-spin" />
        <p className="text-sm text-slate-400">Verifying QR code...</p>
      </div>
    );
  }

  if (!shopData && error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center">
        <div className="max-w-lg mx-auto px-4 w-full">
          <div className="bg-slate-800 border border-slate-700/50 rounded-xl px-6 py-8 text-center">
            <div className="inline-flex items-center rounded-full bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300">
              QR Link Unavailable
            </div>
            <h2 className="mt-4 text-xl font-semibold text-slate-100">
              Unable to open this print session
            </h2>
            <p className="mt-3 text-sm text-slate-400">{error}</p>
            <div className="mt-6 space-y-3 text-sm text-slate-500">
              <p>Ask the shop to show the QR code again, then rescan it from your phone.</p>
              <p>If you already scanned recently, refreshing the page may also help.</p>
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Still resolving location / verifying — don't flash the upload form
  if (!shopData) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen gap-4 bg-slate-950">
        <Loader2 size={32} className="text-indigo-600 animate-spin" />
        <p className="text-sm text-slate-400">Checking location…</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center">
        <div className="max-w-lg mx-auto px-4 w-full">
          <div className="text-center py-12 px-8 bg-slate-800 border border-slate-700/50 rounded-xl">
            <span className="text-xs font-medium text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">
              StatioPrint
            </span>
            <CheckCircle size={64} className="text-emerald-500 mx-auto my-4" />
            <h2 className="text-xl font-semibold text-slate-200 mb-2">
              {paymentMethod === 'online' ? 'Payment Successful!' : 'Upload Successful!'}
            </h2>
            <p className="text-slate-400 mb-6 text-sm">
              {paymentMethod === 'online'
                ? 'Payment received. The shop owner will review and approve your documents before printing.'
                : 'Your documents were uploaded. Please wait for owner approval and pay at the shop counter.'}
            </p>
            <button
              onClick={() => {
                setSuccess(false);
                setError('');
              }}
              className="mt-2 px-6 py-2 border border-slate-700/50 text-slate-200 rounded-lg hover:bg-slate-700/50 transition-colors text-sm"
            >
              Upload More Documents
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-800">
      <div className="max-w-lg mx-auto px-4 pt-8 pb-8">
        <div className="bg-slate-800 border border-slate-700/50 rounded-xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <img
              src="https://assets-statiomart.s3.ap-south-1.amazonaws.com/statiomart.png"
              alt="StatioPrint"
              className="w-8 h-8 object-cover rounded-lg"
            />
            <span className="text-xs font-medium text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">
              StatioPrint
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-200 mb-1">
            {shopData?.shopName || 'Print Shop'}
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            Upload your document and choose your print preferences
          </p>

          <div className="flex flex-col gap-5 mt-6">
            {/* Customer Name & Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Your Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  className="w-full bg-transparent border border-slate-700/50 rounded-lg px-4 py-3 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition-colors"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Phone Number</label>
                <div className="flex items-center bg-transparent border border-slate-700/50 rounded-lg px-3 py-3 focus-within:border-indigo-600 transition-colors gap-2">
                  <span className="text-slate-300 text-sm pr-2 border-r border-slate-700 min-w-[42px] text-center">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={customerPhoneInput}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setCustomerPhoneInput(digits);
                    }}
                    required
                    className="w-full bg-transparent border-none outline-none px-3 text-slate-200 placeholder-slate-500 text-sm"
                    placeholder="10-digit mobile number"
                  />
                </div>
                {!isPhoneValid && customerPhoneInput.length > 0 && (
                  <p className="text-xs text-amber-400 mt-1">Enter a valid Indian mobile number.</p>
                )}
              </div>
            </div>

            {/* Drag and drop file area */}
            <div>
              <label
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`block border-2 border-dashed rounded-lg py-6 px-4 text-center cursor-pointer transition-all ${
                  isDragOver
                    ? 'border-indigo-600 bg-indigo-600/[0.08]'
                    : 'border-slate-700/50 hover:border-indigo-600 hover:bg-indigo-600/5'
                }`}
              >
                {documentCount > 0 ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText size={20} className="text-indigo-600" />
                    <span className="text-sm text-slate-200 truncate max-w-[220px]">
                      {documentCount} {documentCount === 1 ? 'document' : 'documents'} selected
                    </span>
                  </div>
                ) : (
                  <>
                    <Upload size={36} className="text-slate-600 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      Drag & drop your files here, or click to browse
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      PDF, DOCX, images, or ZIP containing those files
                    </p>
                  </>
                )}
                <input
                  type="file"
                  multiple
                  accept=".pdf,.docx,.png,.jpg,.jpeg,.gif,.bmp,.tif,.tiff,.webp,.svg,.zip,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/gif,image/bmp,image/tiff,image/webp,image/svg+xml,application/zip,application/x-zip-compressed"
                  hidden
                  onChange={handleFileChange}
                />
              </label>

              {files.length > 0 && (
                <div className="mt-3 space-y-2 max-h-48 overflow-auto pr-1">
                  {files.map((selectedFile) => {
                    return (
                      <div
                        key={`${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`}
                        className="flex items-center gap-2 rounded-lg border border-slate-700/50 bg-slate-900/50 px-3 py-2"
                      >
                        <FileText size={15} className="text-indigo-400 shrink-0" />
                        <span className="flex-1 truncate text-sm text-slate-200">
                          {selectedFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewFile(selectedFile);
                            setShowPreview(true);
                          }}
                          className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-indigo-300 transition-colors"
                          title="Preview"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(selectedFile)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-red-300 transition-colors"
                          title="Remove document"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Manual Page Count (for PDFs / DOCX) */}
            {files.length > 0 && !imageOnlySelection && (
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">
                  Number of Pages{' '}
                  <span className="text-slate-600 font-normal">(auto-detected for PDFs)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={manualPageCount}
                  onChange={(e) =>
                    setManualPageCount(e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))
                  }
                  className="w-full bg-transparent border border-slate-700/50 rounded-lg px-4 py-3 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition-colors"
                  placeholder="e.g. 10"
                />
                {paymentMethod === 'online' && !manualPageCount && (
                  <p className="text-xs text-amber-400 mt-1">Required for online payment.</p>
                )}
              </div>
            )}

            {/* Paper Size & Type Row */}
            <div className="grid grid-cols-2 gap-3">
              {/* Paper Size */}
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Paper Size</label>
                <select
                  value={paperSize}
                  onChange={(e) => setPaperSize(e.target.value as PaperSize)}
                  className="w-full bg-transparent border border-slate-700/50 rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-indigo-600 transition-colors appearance-none text-sm"
                >
                  {supportedPaperSizes.map((size) => (
                    <option key={size} value={size} className="bg-slate-800">
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              {/* Paper Type */}
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Paper Type</label>
                <select
                  value={paperType}
                  onChange={(e) => setPaperType(e.target.value as PaperType)}
                  className="w-full bg-transparent border border-slate-700/50 rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-indigo-600 transition-colors appearance-none text-sm"
                >
                  {supportedPaperTypes.map((type) => (
                    <option key={type} value={type} className="bg-slate-800">
                      {PAPER_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Orientation Row */}
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Orientation</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setOrientation('portrait')}
                  className={`py-2.5 rounded-lg border text-sm transition-colors ${
                    orientation === 'portrait'
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-slate-700/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  Portrait
                </button>
                <button
                  type="button"
                  onClick={() => setOrientation('landscape')}
                  className={`py-2.5 rounded-lg border text-sm transition-colors ${
                    orientation === 'landscape'
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-slate-700/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  Landscape
                </button>
              </div>
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Quantity</label>
              <div className="flex items-center justify-between border border-slate-700/50 rounded-lg px-2 py-1">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className={`w-8 h-8 flex items-center justify-center rounded-full text-slate-200 transition-colors ${
                    quantity > 1 ? 'hover:bg-indigo-600/15' : 'opacity-30'
                  }`}
                  disabled={quantity <= 1}
                >
                  <Minus size={16} />
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                  min={1}
                  className="bg-transparent border-none outline-none text-slate-200 text-lg font-semibold text-center w-16"
                />
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-full text-slate-200 transition-colors hover:bg-indigo-600/15"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Print Type */}
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Print Type</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setColor('bw')}
                  className={`flex-1 py-2.5 rounded-lg border text-sm transition-colors ${
                    color === 'bw'
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-slate-700/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  Black & White
                </button>
                <button
                  onClick={() => setColor('color')}
                  className={`flex-1 py-2.5 rounded-lg border text-sm transition-colors ${
                    color === 'color'
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-slate-700/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  Color
                </button>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Payment Method</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setPaymentMethod('online')}
                  className={`flex-1 py-2.5 rounded-lg border text-sm transition-colors ${
                    paymentMethod === 'online'
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-slate-700/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  Pay Online
                </button>
                <button
                  onClick={() => setPaymentMethod('cod')}
                  className={`flex-1 py-2.5 rounded-lg border text-sm transition-colors ${
                    paymentMethod === 'cod'
                      ? 'border-indigo-600 bg-indigo-600 text-white'
                      : 'border-slate-700/50 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  Pay at Shop
                </button>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="bg-slate-950/50 border border-slate-700/50 rounded-xl p-4">
              <p className="text-sm text-slate-400 mb-3 font-medium">Price Breakdown</p>
              {imageOnlySelection ? (
                <>
                  {baseCharge > 0 && (
                    <div className="flex justify-between mb-1.5 text-sm">
                      <span className="text-slate-500">Base Charge per document:</span>
                      <span className="text-slate-200">
                        {'\u20B9'}
                        {baseCharge}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between mb-1.5 text-sm">
                    <span className="text-slate-500">
                      {documentCount} pages x {'\u20B9'}
                      {perPagePrice}:
                    </span>
                    <span className="text-slate-200">
                      {'\u20B9'}
                      {documentCount * perPagePrice}
                    </span>
                  </div>
                  {copies > 1 && (
                    <div className="flex justify-between mb-1.5 text-sm">
                      <span className="text-slate-500">x {copies} copies:</span>
                      <span className="text-slate-200">
                        {'\u20B9'}
                        {calculatePrice}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between mb-1.5 text-sm">
                    <span className="text-slate-500">Documents:</span>
                    <span className="text-slate-200">x {documentCount}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-700/50 pt-2.5 mt-2">
                    <span className="text-base font-bold text-slate-200">Estimated Total:</span>
                    <span className="text-base font-bold text-indigo-500">
                      {'\u20B9'}
                      {calculatePrice}
                    </span>
                  </div>
                </>
              ) : effectivePageCount > 0 ? (
                <>
                  {baseCharge > 0 && (
                    <div className="flex justify-between mb-1.5 text-sm">
                      <span className="text-slate-500">Base Charge per document:</span>
                      <span className="text-slate-200">
                        {'\u20B9'}
                        {baseCharge}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between mb-1.5 text-sm">
                    <span className="text-slate-500">
                      {manualPageCount} pages x {'\u20B9'}
                      {perPagePrice}:
                    </span>
                    <span className="text-slate-200">
                      {'\u20B9'}
                      {(manualPageCount as number) * perPagePrice}
                    </span>
                  </div>
                  {copies > 1 && (
                    <div className="flex justify-between mb-1.5 text-sm">
                      <span className="text-slate-500">x {copies} copies:</span>
                      <span className="text-slate-200">
                        {'\u20B9'}
                        {calculatePrice}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-slate-700/50 pt-2.5 mt-2">
                    <span className="text-base font-bold text-slate-200">Total:</span>
                    <span className="text-base font-bold text-indigo-500">
                      {'\u20B9'}
                      {calculatePrice}
                    </span>
                  </div>
                </>
              ) : (
                <div className="rounded-lg border border-slate-700/50 bg-slate-900/40 px-4 py-3 text-sm text-slate-400">
                  Enter the number of pages above to see the price estimate.
                </div>
              )}
              <p className="text-xs text-slate-600 mt-2">
                {paperSize} • {PAPER_TYPE_LABELS[paperType]} • {orientation} •{' '}
                {color === 'bw' ? 'B&W' : 'Color'} • {copies} {copies === 1 ? 'copy' : 'copies'} •{' '}
                {documentCount} {documentCount === 1 ? 'document' : 'documents'}
              </p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={
                !canSubmit ||
                !customerName ||
                !isPhoneValid ||
                uploading ||
                (paymentMethod === 'online' && !imageOnlySelection && !manualPageCount)
              }
              className="w-full py-3 rounded-lg font-semibold text-white bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {uploading
                ? 'Processing...'
                : paymentMethod === 'online'
                  ? `Pay \u20B9${calculatePrice} & Upload ${documentCount} ${documentCount === 1 ? 'Document' : 'Documents'}`
                  : `Upload ${documentCount} ${documentCount === 1 ? 'Document' : 'Documents'} for COD`}
            </button>
            <p className="text-xs text-slate-500 text-center">
              Need help? Contact{' '}
              <a href="mailto:info@statiomart.com" className="text-indigo-400 hover:underline">
                info@statiomart.com
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && filePreview && previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => {
            setShowPreview(false);
            setPreviewFile(null);
          }}
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-xl p-4 max-w-2xl max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-200">Document Preview</h3>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewFile(null);
                }}
                className="text-slate-400 hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <div
              className={`bg-white rounded-lg overflow-hidden flex items-center justify-center ${
                orientation === 'landscape' ? 'w-96 h-64' : 'w-64 h-96'
              }`}
              style={{ margin: '0 auto' }}
            >
              {filePreview.startsWith('data:image') ? (
                <img
                  src={filePreview}
                  alt="Preview"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400 p-8">
                  <FileText size={48} className="mb-2" />
                  <p className="text-sm">{previewFile.name}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {filePreview === 'pdf' ? 'PDF Document' : 'Document'}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4 text-center text-xs text-slate-500">
              {paperSize} • {orientation} • {copies} {copies === 1 ? 'copy' : 'copies'}
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Loader */}
      <FullScreenLoader
        isVisible={uploading}
        message={loadingMessage.title}
        subMessage={loadingMessage.subtitle}
      />
    </div>
  );
}
